import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { useLazyUrlCheckerQuery } from '@/app/services/chatbot-rtk.services';
import { PSButton } from '@/components/ui/button';
import InfoMessageAlert from '@/features/bot/InfoMessageAlert';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { selectCurrentDataState } from '../../create.slice';

const SetDomain = () => {
  const { t } = useTranslation();
  const { control, setValue, formState, watch, setError, clearErrors, getValues } = useFormContext();

  const currentData = useAppSelector(selectCurrentDataState);
  const [checkUrl, checkUrlResults] = useLazyUrlCheckerQuery();
  const [isValidDomain, setIsValidDomain] = useState(false);

  const handleTestSiteMapExistence = () => {
    setIsValidDomain(false);

    if (
      !watch('plug_and_play.address') ||
      watch('plug_and_play.address') === '' ||
      !/^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z()]{2,6}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/.test(watch('plug_and_play.address'))
    )
      return setError('plug_and_play.address', {
        type: 'custom',
        message: t('Wizard/PlugAndPlayInfos/key15'),
      });

    checkUrl(`https://${getValues('plug_and_play.address')}`)
      .unwrap()
      .then(res => {
        setIsValidDomain(true);
        setValue(
          'plug_and_play',
          {
            ...(currentData.plug_and_play || {}),
            address: res.address.replace('https://', ''),
            links: [],
            isVerified: true,
            sitemap: res.links,
          },
          { shouldValidate: true }
        );

        clearErrors('plug_and_play');
      })
      .catch(err => {
        setIsValidDomain(false);
        setValue('plug_and_play.isVerified', false);

        setError('plug_and_play.address', {
          type: 'custom',
          message: t('Wizard/PlugAndPlayInfos/key13'),
        });
      });
  };
  useEffect(() => {
    clearErrors();

    if (currentData?.plug_and_play) {
      setValue('plug_and_play', {
        ...(currentData.plug_and_play || {}),
        address: currentData?.plug_and_play?.address?.replace('https://', '') || '',
        links: currentData?.plug_and_play?.links || [],
        isVerified: currentData?.plug_and_play?.isVerified || false,
        sitemap: currentData?.plug_and_play?.sitemap || [],
      });
    }
  }, [currentData]);

  return (
    <div className="plug-and-play tw-py-4 md:tw-px-4">
      <div className="tw-w-full tw-min-h-[166px] tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
        <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
          <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
            <div className="tw-self-stretch tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
              1. {t('Wizard/PlugAndPlayInfos/key1')}
            </div>
            <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t('Wizard/PlugAndPlayInfos/key2')}
            </div>
          </div>
        </div>

        <InfoMessageAlert texts={[t('Wizard/PlugAndPlayAlert/key1')]} />

        <div className="tw-self-stretch tw-min-h-[82px] tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
          <div className="tw-self-stretch tw-min-h-[82px] tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
            <label htmlFor="plug_and_play.address" className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-block">
              {t('Wizard/PlugAndPlayInfos/key11')}
            </label>

            <div className="md:tw-w-2/3 tw-w-full tw-self-stretch tw-min-h-[38px] tw-rounded-md tw-shadow tw-border tw-border-gray-200 tw-justify-start tw-items-center tw-inline-flex">
              <div className="tw-pl-[13px] tw-pr-3 tw-py-[9px] tw-bg-gray-50 tw-rounded-tl-md tw-rounded-bl-md tw-border tw-border-gray-100 tw-justify-start tw-items-center tw-flex">
                <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">https://</div>
              </div>

              <Controller
                name="plug_and_play.address"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    placeholder="example.com/sitemap.xml"
                    isInvalid={!!formState.errors?.plug_and_play?.address}
                    className="tw-grow tw-shrink tw-basis-0 tw-min-h-[38px] tw-pl-3 tw-pr-[13px] tw-py-[9px] tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed tw-rounded-tr-md tw-rounded-br-md tw-justify-start tw-items-center tw-flex placeholder:tw-text-gray-400 tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-border-0 tw-w-full focus:tw-ring-0 focus:tw-outline-none"
                  />
                )}
              />

              <PSButton
                id="btn-set-domain-test"
                onClick={handleTestSiteMapExistence}
                disabled={!!(formState.errors.plug_and_play as any)?.address || watch('plug_and_play.address') == '' || checkUrlResults.isFetching}
                isLoading={checkUrlResults.isFetching}
                title={t('Wizard/PlugAndPlayInfos/key16')}
              />
            </div>

            {checkUrlResults.isFetching && <p className="tw-text-sm tw-text-sky-600">{t('Wizard/PlugAndPlayInfos/key17')}</p>}

            <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"></div>
              <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('Wizard/PlugAndPlayInfos/key12')}
              </div>
            </div>

            {isValidDomain && <p className="tw-text-sm tw-text-green-600">{t('Wizard/PlugAndPlayInfos/key18')}</p>}

            {!!(formState.errors.plug_and_play as any)?.address && (
              <span className="tw-text-sm tw-text-red-500 tw-block">{(formState.errors?.plug_and_play as any)?.address?.message as string}</span>
            )}
            {!(formState.errors.plug_and_play as any)?.address && !!(formState.errors.plug_and_play as any)?.isVerified && (
              <span className="tw-text-sm tw-text-red-500 tw-block">{(formState.errors?.plug_and_play as any)?.isVerified?.message as string}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(SetDomain);
